import {Component, ViewChild, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {ToastService} from "../../services/Toast.service";
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {Session} from "../../services/Session";
import {AddUserService} from "../../tpiq-services/add-user.service";
import {SwitchBoard} from "../../services/SwitchBoard";
import {LoadingService} from "../../services/LoadingService";
import {PageTitleService} from "../../services/PageTitle";
import {StateService} from "../../services/StateService";
import {Router, NavigationEnd} from '@angular/router';

import {takeUntil, map, switchMap, catchError, filter} from 'rxjs/operators';
import {Observable, of as observableOf, throwError} from 'rxjs';
import {validatePassword} from '../../utils/password.utils';

import {FormsModule} from "@angular/forms";
import {FoundationInit} from "../../directives/foundation.directive";

import {LoginService} from "../../services/Login.service";
import {TaxPlanningService} from "../../tpiq-services/tax-planning.service";

declare const _: any;

@Component({
  selector: 'app-manage-user-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, FoundationInit],
  templateUrl: './manage-user-profile.component.html',
  styleUrl: './manage-user-profile.component.scss'
})


export class ManageUserProfileComponent  implements OnInit, OnDestroy {
    org: any;
    user: string;
    destroy$: EventEmitter<void> = new EventEmitter<void>();
    userProfileData: any = {
    firstName: '',
    lastName: '',
    id: '',
    phoneNumber: '',
    countryCode: '+1'
    };

    firmName: string = '';
    initialFirmName: string = '';

    firmDisclaimer: string = '';
    initialFirmDisclaimer: string = '';

    initialUserProfileData: any = null;

    showChangePassword: boolean = false;

    newPassword: string = '';
    confirmPassword: string = '';
    passwordErrors: string[] = [];

    constructor(
        private toastService: ToastService,
        private addUserService: AddUserService,
        private switchBoard: SwitchBoard,
        private loadingService: LoadingService,
        private titleService: PageTitleService,
        private stateService: StateService,
        private _router: Router,
        private loginService: LoginService,
        private taxPlanningService: TaxPlanningService,
       ) {
    }

    ngOnInit() {
        this._inIt();
    }

    ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

    _inIt() {
        const base = this;
        this.org = Session.getOrg();
        // Ensure we always have the logged-in user id
        const sessionUser: any = Session.getUser();
        this.user = sessionUser?.id;
        this.firmName = (sessionUser?.org || sessionUser?.orgName || sessionUser?.firmName || '').toString();
        this.initialFirmName = this.firmName;

        this.titleService.setPageTitle('USER PROFILE');
        this.getUserProfileData(this.user);
        this.loadFirmInfo();
        this.loadFirmDisclaimer();
        this.switchBoard.onPageChangeListener.next({});
        this.switchBoard.onToggleFeedback.next({
          display: true,
          pageName: 'Profile page'
        });

        this.switchBoard.onClickPrev
            .pipe(takeUntil(this.destroy$)).subscribe(title => {
              this.gotoDefaultPage();
        });

        // If Angular reuses this component instance, ngOnInit won't re-run on back/forward.
        // Refresh fields on navigation events so the view reflects latest DB values.
        this._router.events
          .pipe(
            takeUntil(this.destroy$),
            filter((e: any) => e instanceof NavigationEnd)
          )
          .subscribe(() => {
            // Re-load from APIs; keep firmName prefill fallback, but overwrite with server values when available.
            this.getUserProfileData(this.user);
            this.loadFirmInfo();
            this.loadFirmDisclaimer();
          });
    }

    getUserProfileData(user) {
      const base = this;
      this.loadingService.triggerLoadingEvent(true);
      this.addUserService.getUserProfileData(this.org, user)
        .subscribe(response => {
          this.userProfileData = response;
          this.initialUserProfileData = JSON.parse(JSON.stringify(this.userProfileData));
          this.loadingService.triggerLoadingEvent(false);
        }, error => {
          error = JSON.parse(error);
          const message = error.message ? error.message : 'Failed to get User Details';
          this.toastService.pop(TOAST_TYPE.error, message);
          this.loadingService.triggerLoadingEvent(false);
        });
    }

    loadFirmInfo() {
      // Fetch firm/company info to populate firmName for display/edit.
      this.loginService.getOrgCompany(this.org)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          // Firm name can come back with different keys depending on API shape
          const fetchedName = (res && (res.firmName || res.firm_name || res.org_name || res.orgName || res.name)) || '';
          if (fetchedName && fetchedName.trim().length > 0) {
            this.firmName = fetchedName;
            this.initialFirmName = fetchedName;
          }
        }, () => {});
    }

    loadFirmDisclaimer() {
      // Response shape is unconfirmed since this endpoint has no prior consumer; accept a plain string or a wrapping object.
      this.taxPlanningService.getDisclaimer(this.user, this.org)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          const fetchedDisclaimer = typeof res === 'string' ? res : ((res && (res.disclaimer || res.text || res.content)) || '');
          this.firmDisclaimer = fetchedDisclaimer;
          this.initialFirmDisclaimer = fetchedDisclaimer;
        }, () => {});
    }

    updateFirmName() {
      const firmName = (this.firmName || '').trim();
      if (!firmName) {
        this.toastService.pop(TOAST_TYPE.warning, 'Firm Name is Mandatory');
        return;
      }

      this.loadingService.triggerLoadingEvent(true);
      this.loginService.updateCompanyName(this.org, this.user, firmName)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadingService.triggerLoadingEvent(false);
          this.toastService.pop(TOAST_TYPE.success, 'Firm name updated successfully');
          this.initialFirmName = firmName;
          // Keep session in sync so header/org displays reflect changes without re-login
          try {
            const su: any = Session.getUser();
            if (su) {
              su.org = firmName;
              su.orgName = firmName;
              su.firmName = firmName;
              Session.put('user', su);
            }
          } catch (e) {
            // no-op
          }
          // Re-fetch firm info so UI reflects persisted value
          this.loadFirmInfo();
        }, (error: any) => {
          this.loadingService.triggerLoadingEvent(false);
          let message = 'Failed to update firm name';
          try {
            const parsed = typeof error === 'string' ? JSON.parse(error) : error;
            message = parsed && parsed.message ? parsed.message : message;
          } catch (e) {
            // ignore
          }
          this.toastService.pop(TOAST_TYPE.error, message);
        });
    }

    toggleChangePassword() {
      this.showChangePassword = !this.showChangePassword;
      if (!this.showChangePassword) {

        this.newPassword = '';
        this.confirmPassword = '';
        this.passwordErrors = [];
      }
    }


    updatePassword() {

      const pw = this.newPassword || '';
      const confirm = this.confirmPassword || '';
      

      this.passwordErrors = validatePassword(pw);

      if (this.passwordErrors.length > 0) {
        this.toastService.pop(TOAST_TYPE.warning, 'Password does not meet requirements');
        return;
      }

      if (pw !== confirm) {
        this.toastService.pop(TOAST_TYPE.warning, 'Passwords do not match');
        return;
      }

      this.loadingService.triggerLoadingEvent(true);
      // Existing API expects a user object; send minimal payload.

      this.loginService.updatePassword({ id: this.user, password: pw })

        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadingService.triggerLoadingEvent(false);
          this.toastService.pop(TOAST_TYPE.success, 'Password updated successfully');
          this.toggleChangePassword();
        }, (error: any) => {
          this.loadingService.triggerLoadingEvent(false);
          let message = 'Failed to update password';
          try {
            const parsed = typeof error === 'string' ? JSON.parse(error) : error;
            message = parsed && parsed.message ? parsed.message : message;
          } catch (e) {
            // ignore
          }
          this.toastService.pop(TOAST_TYPE.error, message);
        });
    }

   gotoDefaultPage() {
    const prevState = this.stateService.getPrevState();
    if (prevState.key === 'Tools') {
      this._router.navigate([prevState.url]);
    } else if (prevState) {
      this.stateService.pop();
      this._router.navigate([prevState.url]);
    }
  }

    validate(obj) {
      let validateUser = true;
      if (!obj.firstName.trim()) {
        this.toastService.pop(TOAST_TYPE.warning, 'First Name is Mandatory');
        validateUser = false;
      }else if (!obj.lastName.trim()) {
        this.toastService.pop(TOAST_TYPE.warning, 'Last Name is Mandatory');
        validateUser = false;
      }else if (!obj.id.trim()) {
        this.toastService.pop(TOAST_TYPE.warning, 'Email is Mandatory');
        validateUser = false;
      }

      return validateUser;
    }

    updateUserInfo() {
      const userInfoData = this.userProfileData;
      const obj = {
        firstName: userInfoData.firstName,
        lastName: userInfoData.lastName,
        id: userInfoData.id,
        phoneNumber: userInfoData.phoneNumber,
        countryCode :  userInfoData.countryCode,
      };

      if (this.validate(obj)) {
        this.updateUser(obj);
      }
    }

    updateUser(obj) {
      this.loadingService.triggerLoadingEvent(true);
      this.addUserService.updateUser(obj)
        .subscribe(success  => {
          this.loadingService.triggerLoadingEvent(false);
          this.toastService.pop(TOAST_TYPE.success, 'Profile updated successfully');
          // Refresh baseline and re-fetch to reflect persisted values
          // Sync Session user so header dropdown (uses user.first_name/last_name) updates without re-login
          try {
            const su: any = Session.getUser();
            if (su) {
              su.first_name = this.userProfileData?.firstName;
              su.last_name  = this.userProfileData?.lastName;

              // keep camelCase mirrors too (other parts of app may use them)
              su.firstName = this.userProfileData?.firstName;
              su.lastName  = this.userProfileData?.lastName;
              Session.put('user', su);
            }
          } catch (e) {
            // no-op
          }
          this.initialUserProfileData = JSON.parse(JSON.stringify(this.userProfileData));
          this.getUserProfileData(this.user);
        }, error =>  {
          error = JSON.parse(error);
          const message = error && error.message ? error.message : 'Failed To Update Profile';
          this.toastService.pop(TOAST_TYPE.error, message);
          this.loadingService.triggerLoadingEvent(false);
        });
    }

    private norm(v: any): string {
      if (v === null || v === undefined) return '';
      return String(v).trim();
    }

    hasUserProfileChanged(): boolean {
      if (!this.initialUserProfileData) {
        // If we don't have a baseline yet, assume changed so updates work.
        return true;
      }
      const cur = this.userProfileData || {};
      const init = this.initialUserProfileData || {};
      return (
        this.norm(cur.firstName) !== this.norm(init.firstName) ||
        this.norm(cur.lastName) !== this.norm(init.lastName) ||
        this.norm(cur.phoneNumber) !== this.norm(init.phoneNumber) ||
        this.norm(cur.countryCode) !== this.norm(init.countryCode)
      );
    }

    updateProfileAndFirm() {
      const firmNameNow = (this.firmName || '').trim();
      const firmNameBefore = (this.initialFirmName || '').trim();
      const firmChanged = !!firmNameNow && firmNameNow !== firmNameBefore;

      const firmDisclaimerNow = (this.firmDisclaimer || '').trim();
      const firmDisclaimerBefore = (this.initialFirmDisclaimer || '').trim();
      const disclaimerChanged = firmDisclaimerNow !== firmDisclaimerBefore;

      const profileChanged = this.hasUserProfileChanged();


      const pw = (this.newPassword || '').trim();
      const confirm = (this.confirmPassword || '').trim();
      const passwordChanged = !!pw && pw.length > 0;

      // Validate password if it's being changed
      if (passwordChanged) {
        this.passwordErrors = validatePassword(pw);
        if (this.passwordErrors.length > 0) {
          this.toastService.pop(TOAST_TYPE.warning, 'Password does not meet requirements');
          return;
        }
        if (pw !== confirm) {
          this.toastService.pop(TOAST_TYPE.warning, 'Passwords do not match');
          return;
        }
      }

      if (!firmChanged && !disclaimerChanged && !profileChanged && !passwordChanged) {

        this.toastService.pop(TOAST_TYPE.info, 'No changes to update');
        return;
      }


      const runProfileAndPasswordUpdate = (): Observable<any> => {
        if (!profileChanged && !passwordChanged) {
          return observableOf(null);
        }

        const userInfoData = this.userProfileData;
        const payload: any = {
          firstName: userInfoData.firstName,
          lastName: userInfoData.lastName,
          id: userInfoData.id,
          phoneNumber: userInfoData.phoneNumber,
          countryCode: userInfoData.countryCode,
        };

        // Only include password if being changed
        if (passwordChanged) {
          payload.password = pw;
        }

        // Only validate profile fields if profile is changing
        if (profileChanged && !this.validate(payload)) {
          return throwError('Validation failed');
        }

        return this.loginService.updateUserProfile(payload).pipe(
          map(() => {
            if (profileChanged) {
              this.toastService.pop(TOAST_TYPE.success, 'Profile updated successfully');
              try {
                const su: any = Session.getUser();
                if (su) {
                  su.first_name = this.userProfileData?.firstName;
                  su.last_name = this.userProfileData?.lastName;
                  su.firstName = this.userProfileData?.firstName;
                  su.lastName = this.userProfileData?.lastName;
                  Session.put('user', su);
                }
              } catch (e) {
                // no-op
              }
              this.initialUserProfileData = JSON.parse(JSON.stringify(this.userProfileData));
              this.getUserProfileData(this.user);
            }

            if (passwordChanged) {
              this.toastService.pop(TOAST_TYPE.success, 'Password updated successfully');
              this.newPassword = '';
              this.confirmPassword = '';
              this.passwordErrors = [];
              this.showChangePassword = false;
            }

            return null;
          }),
          catchError((error: any) => {
            let message = 'Failed to update profile';
            try {
              const parsed = typeof error === 'string' ? JSON.parse(error) : error;
              message = parsed && parsed.message ? parsed.message : message;
            } catch (e) {
              // ignore
            }
            this.toastService.pop(TOAST_TYPE.error, message);
            return throwError(error);
          })
        );
      };

      const runFirmUpdate = (): Observable<any> => {
        if (!firmChanged) {
          return observableOf(null);
        }
        const firmName = firmNameNow;
        return this.loginService.updateCompanyName(this.org, this.user, firmName)
          .pipe(
            takeUntil(this.destroy$),
            map(() => {
              this.toastService.pop(TOAST_TYPE.success, 'Firm name updated successfully');
              this.initialFirmName = firmName;
              try {
                const su: any = Session.getUser();
                if (su) {
                  su.org = firmName;
                  su.orgName = firmName;
                  su.firmName = firmName;
                  Session.put('user', su);
                }
              } catch (e) {
                // no-op
              }
              this.loadFirmInfo();
              return null;
            }),
            catchError((error: any) => {
              let message = 'Failed to update firm name';
              try {
                const parsed = typeof error === 'string' ? JSON.parse(error) : error;
                message = parsed && parsed.message ? parsed.message : message;
              } catch (e) {
                // ignore
              }
              this.toastService.pop(TOAST_TYPE.error, message);
              return throwError(error);
            })
          );
      };

      const runDisclaimerUpdate = (): Observable<any> => {
        if (!disclaimerChanged) {
          return observableOf(null);
        }
        const disclaimerText = firmDisclaimerNow;
        return this.taxPlanningService.saveDisclaimer(this.user, this.org, {disclaimer: disclaimerText})
          .pipe(
            takeUntil(this.destroy$),
            map(() => {
              this.toastService.pop(TOAST_TYPE.success, 'Firm disclaimer updated successfully');
              this.initialFirmDisclaimer = disclaimerText;
              return null;
            }),
            catchError((error: any) => {
              let message = 'Failed to update firm disclaimer';
              try {
                const parsed = typeof error === 'string' ? JSON.parse(error) : error;
                message = parsed && parsed.message ? parsed.message : message;
              } catch (e) {
                // ignore
              }
              this.toastService.pop(TOAST_TYPE.error, message);
              return throwError(error);
            })
          );
      };

      // Chain updates sequentially: firm -> disclaimer -> (profile + optional password)
      this.loadingService.triggerLoadingEvent(true);

      let updateChain: Observable<any> = observableOf(null);

      if (firmChanged) {
        updateChain = runFirmUpdate();
      }

      if (disclaimerChanged) {
        updateChain = updateChain.pipe(switchMap(() => runDisclaimerUpdate()));
      }

      if (profileChanged || passwordChanged) {
        updateChain = updateChain.pipe(
          switchMap(() => runProfileAndPasswordUpdate())
        );
      }

      updateChain
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => this.loadingService.triggerLoadingEvent(false),
          error: () => this.loadingService.triggerLoadingEvent(false),
        });

    }
  }